import React, { useState } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/styles";
import {
  createMuiTheme,
  MuiThemeProvider,
  CssBaseline,
  Container,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import palette from "../../theme/palette";
import { AccountNotFound } from "../account/components";
import { SummaryCharts } from "./SummaryCharts";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useSummaryData } from "./hooks/useSummaryData";
import { useSubscriptionTable } from "./hooks/useSubscriptionTable";
import { SubscriptionTable } from "./components/SubscriptionTable";
import { ActiveSubscriptionTable } from "./components/ActiveSubscriptionTable";
import { CanceledSubscriptionTable } from "./components/CanceledSubscriptionTable";
import { DataTable } from "./components/DataTable";
import { SummaryHeader } from "./components/SummaryHeader";
import { TrialSubsTable } from "./components/TrialSubsTable";
import {
  transformSubscriptionsData,
  transformCanceledSubscriptionsData,
  transformInvitesData,
  transformTrialSubsData,
  transformNYCampaignData,
  transformCollCountsData,
} from "./utils/dataTransformers";
import { createExportActions } from "./components/DataTable";

const useStyles = (theme) => ({
  paper: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  root: {
    marginTop: theme.spacing(4),
  },
  buttonLink: {
    width: "100%",
  },
  linkedButton: {
    margin: theme.spacing(1, 0),
  },
});

const theme = createMuiTheme({
  typography: {
    h6: {
      fontWeight: 500,
      fontSize: "20px",
      letterSpacing: "-0.05px",
      lineHeight: "20px",
    },
  },
  palette: {
    secondary: {
      main: palette.primary.main,
    },
  },
});

function Summary(props) {
  const classes = props.classes;
  const [selectedRows] = useState({
    invites: [],
    subscriptions: [],
    subscriptionsCanceled: [],
    trialSubs: [],
    nyCampaignUsers: [],
  });

  // Fetch and process all data
  const {
    uid,
    role,
    error,
    processedData,
    usersData,
    deviceLimitExceptionList,
    invites,
    nyCampaignUsers,
    collCounts,
    trialSubs,
    staffStudents,
    isLoading,
    hasError,
  } = useSummaryData();

  // Manage subscription table state
  const subscriptionTableState = useSubscriptionTable(
    processedData.totalDataValues,
    processedData.totalActiveDataValues,
    processedData.totalCanceledDataValues
  );

  // Transform data for tables
  const subscriptionsData = transformSubscriptionsData(
    processedData.subscriptions
  );
  const subscriptionsCanceledData = transformCanceledSubscriptionsData(
    processedData.allCanceledUsers
  );
  const invitesData = transformInvitesData(invites);
  const trialSubsData = transformTrialSubsData(trialSubs);
  const nyData = transformNYCampaignData(nyCampaignUsers);
  const collCountsData = transformCollCountsData(collCounts);

  // Check authorization
  const isAuthorized = role === "admin" || role === "data-admin";
  const showContent = isAuthorized && uid && !error;

  return (
    <div>
      {showContent ? (
        <Container className={classes.paper} component="main">
          <CssBaseline />
          <SummaryHeader classes={classes} />
          <div style={{ width: "100%" }}>
            <MuiThemeProvider theme={theme}>
              <SummaryCharts
                users={usersData.users || []}
                subscriptions={processedData.subscriptions || []}
                recurringSubscriptions={processedData.subscriptions || []}
                canceledSubscriptions={processedData.subscriptions || []}
                sessions={[]}
                videos={[]}
                issues={[]}
                analysis={[]}
                staffAccounts={staffStudents || []}
                deviceLimitExceptionList={deviceLimitExceptionList || []}
              />

              {/* Subscription Tables */}
              <SubscriptionTable
                data={subscriptionTableState.totalData}
                selectionRange={subscriptionTableState.selectionRange}
                onRangeChange={subscriptionTableState.handleSelectRange}
                openDialog={subscriptionTableState.openRangeDialog}
                onOpenDialog={subscriptionTableState.setOpenRangeDialog}
                selectedPlan={subscriptionTableState.selectedPlans}
                onPlanChange={subscriptionTableState.handleSubsPlan}
              />

              <ActiveSubscriptionTable
                data={subscriptionTableState.activeData}
                selectionRange={subscriptionTableState.selectionRangeActive}
                onRangeChange={subscriptionTableState.handleSelectRangeActive}
                openDialog={subscriptionTableState.openRangeDialogActive}
                onOpenDialog={subscriptionTableState.setOpenRangeDialogActive}
                selectedPlan={subscriptionTableState.selectedPlansActive}
                onPlanChange={subscriptionTableState.handleSubsPlanActive}
              />

              <CanceledSubscriptionTable
                data={subscriptionTableState.totalCanceledData}
                selectionRange={subscriptionTableState.selectionRangeCanceled}
                onRangeChange={subscriptionTableState.handleSelectRangeCanceled}
                openDialog={subscriptionTableState.openRangeDialogCanceled}
                onOpenDialog={subscriptionTableState.setOpenRangeDialogCanceled}
                selectedPlan={subscriptionTableState.selectedPlansCanceled}
                onPlanChange={subscriptionTableState.handleSubsPlanCanceled}
              />

              {/* Collection Counts Table */}
              <DataTable
                title="Collection Count"
                description="This table contains the count for different collections in our database"
                data={collCountsData}
                tableType="collCount"
                exportFileName="Collection Count"
                options={{
                  selection: false,
                  sorting: false,
                  search: false,
                  paging: false,
                  rowStyle: (x) => {
                    if (x.tableData.id % 2) {
                      return { backgroundColor: "#f2f2f2" };
                    }
                  },
                }}
              />

              {/* Subscriptions Table */}
              <DataTable
                title="Subscriptions"
                description="Contains subscriptions data purchased by a user"
                data={subscriptionsData}
                tableType="subscriptions"
                selectedRows={selectedRows.subscriptions}
                exportFileName="Subscriptions"
                actions={createExportActions("subscriptions", "Subscriptions")}
                options={{
                  selection: true,
                  sorting: true,
                }}
              />

              {/* Canceled Subscriptions Table */}
              <DataTable
                title="Canceled Subscriptions"
                data={subscriptionsCanceledData}
                tableType="subscriptionsCanceled"
                selectedRows={selectedRows.subscriptionsCanceled}
                exportFileName="Canceled Subscriptions"
                options={{
                  selection: true,
                  sorting: true,
                }}
              />

              {/* Invites Table */}
              <DataTable
                title="Invites"
                data={invitesData}
                tableType="invites"
                selectedRows={selectedRows.invites}
                exportFileName="Invites"
                options={{
                  selection: true,
                  sorting: true,
                }}
              />

              {/* Trial Subscriptions Table */}
              <TrialSubsTable
                data={trialSubsData}
                selectedRows={selectedRows.trialSubs}
              />

              {/* NY Campaign Users Table */}
              <DataTable
                title="NY Campaign Users"
                description="All Users that signed up for the NY Campaign"
                data={nyData}
                tableType="nyCampaign"
                selectedRows={selectedRows.nyCampaignUsers}
                exportFileName="NY Campaign Users"
                options={{
                  selection: true,
                  sorting: true,
                }}
              />

              {/* Loading screen */}
              <Grid
                style={{
                  margin: "auto",
                  padding: 100,
                  display: isLoading ? "block" : "none",
                }}
              >
                <CircularProgress />
              </Grid>

              {/* Error screen */}
              <Grid
                item
                lg={12}
                md={12}
                xl={12}
                xs={12}
                style={{
                  display: error && !isLoading ? "block" : "none",
                }}
              >
                <AccountNotFound error={{ error: error }} />
              </Grid>
            </MuiThemeProvider>
          </div>
        </Container>
      ) : (
        <Grid
          item
          lg={12}
          md={12}
          xl={12}
          xs={12}
          style={{
            display: error && !isLoading ? "block" : "none",
          }}
        >
          <AccountNotFound error={{ error: error }} />
        </Grid>
      )}
    </div>
  );
}

Summary.propTypes = {
  classes: PropTypes.object,
  history: PropTypes.object,
};

export default withStyles(useStyles)(Summary);
